// 档1 — Generic WebSocket console. Adapter-agnostic: connects to the WS gateway, streams
// raw frames both ways, decodes text frames, shows size + hex preview for binary frames, and
// tracks connection duration / estimated cost from wsBilling.perMinuteUsd.
//
// It never imports a WsAdapterClient — that's the whole point of 档1 (bare send/receive so
// any adapter's Try-It degrades gracefully here). 档2 playgrounds layer framing on top.

import React from 'react';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import { wsBytes } from './ws-adapter-client.js';

const STATE_META = {
  idle:       { label: 'Disconnected', color: 'var(--fg-4)' },
  connecting: { label: 'Connecting…',  color: 'var(--c-amber)' },
  open:       { label: 'Connected',    color: 'var(--ok)' },
  closing:    { label: 'Closing…',     color: 'var(--c-amber)' },
  closed:     { label: 'Closed',       color: 'var(--fg-4)' },
  error:      { label: 'Error',        color: 'var(--err)' },
};

const fmtDuration = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

// Immutable log append helper — caps the log so a chatty stream can't grow unbounded.
const MAX_LOG = 500;
const appendLog = (log, entry) => {
  const next = log.concat([{ ...entry, ts: Date.now(), _id: (log.length ? log[log.length - 1]._id : 0) + 1 }]);
  return next.length > MAX_LOG ? next.slice(next.length - MAX_LOG) : next;
};

export default function WsConsole({ api, endpoint }) {
  const wsConfig = (endpoint && endpoint._raw && endpoint._raw.wsConfig) || {};
  const wsBilling = (endpoint && endpoint._raw && endpoint._raw.wsBilling) || {};
  const clientPath = wsConfig.clientPath || '/';
  const perMinuteUsd = Number(wsBilling.perMinuteUsd) || 0;

  const [state, setState] = React.useState('idle');
  const [closeInfo, setCloseInfo] = React.useState(null); // { code, reason }
  const [log, setLog] = React.useState([]);
  const [outText, setOutText] = React.useState('');
  const [connectError, setConnectError] = React.useState('');
  const [elapsedMs, setElapsedMs] = React.useState(0);

  const wsRef = React.useRef(null);
  const startRef = React.useRef(0);
  const tickRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  const pushLog = React.useCallback((entry) => setLog((l) => appendLog(l, entry)), []);

  // Tear everything down on unmount (close socket, clear timer).
  React.useEffect(() => () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
  }, []);

  const stopTick = () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };

  const connect = async () => {
    if (wsRef.current) return;
    setConnectError('');
    setCloseInfo(null);
    setState('connecting');
    let token;
    try {
      const minted = await XApi.keyService.getWsToken(endpoint._raw?.id || endpoint.id);
      token = minted && minted.token;
      if (!token) throw new Error('mint returned no token');
    } catch (e) {
      setState('error');
      setConnectError(e && e.message ? e.message : 'Failed to mint ws-token');
      return;
    }

    const url = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, {
      endpoint: endpoint._raw?.id || endpoint.id,
    });
    pushLog({ dir: 'sys', text: `→ connecting ${url}` });

    let ws;
    try {
      const subs = Array.isArray(wsConfig.subprotocols) && wsConfig.subprotocols.length ? wsConfig.subprotocols : undefined;
      ws = new WebSocket(url, XApi.helpers.buildWsCredentialProtocols(token, subs));
    } catch (e) {
      setState('error');
      setConnectError(e && e.message ? e.message : 'WebSocket construction failed');
      return;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      setState('open');
      startRef.current = Date.now();
      setElapsedMs(0);
      stopTick();
      tickRef.current = setInterval(() => setElapsedMs(Date.now() - startRef.current), 250);
      pushLog({ dir: 'sys', text: '✓ open' });
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        pushLog({ dir: 'in', kind: 'text', text: ev.data });
      } else {
        const buf = ev.data; // ArrayBuffer
        const size = buf.byteLength != null ? buf.byteLength : (buf.size || 0);
        pushLog({ dir: 'in', kind: 'binary', size, hex: wsBytes.hexPreview(buf) });
      }
    };
    ws.onerror = () => {
      pushLog({ dir: 'sys', text: '✗ socket error' });
    };
    ws.onclose = (ev) => {
      stopTick();
      wsRef.current = null;
      setState('closed');
      setCloseInfo({ code: ev.code, reason: ev.reason });
      pushLog({ dir: 'sys', text: `✗ closed · code ${ev.code}${ev.reason ? ` · ${ev.reason}` : ''}` });
    };
  };

  const disconnect = () => {
    setState('closing');
    if (wsRef.current) { try { wsRef.current.close(1000, 'client disconnect'); } catch {} }
  };

  const sendText = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !outText) return;
    try {
      ws.send(outText);
      pushLog({ dir: 'out', kind: 'text', text: outText });
      setOutText('');
    } catch (e) {
      pushLog({ dir: 'sys', text: '✗ send failed: ' + (e && e.message ? e.message : e) });
    }
  };

  const sendBinary = async (file) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !file) return;
    try {
      const buf = await file.arrayBuffer();
      ws.send(buf);
      pushLog({ dir: 'out', kind: 'binary', size: buf.byteLength, hex: wsBytes.hexPreview(buf) });
    } catch (e) {
      pushLog({ dir: 'sys', text: '✗ binary send failed: ' + (e && e.message ? e.message : e) });
    }
  };

  const isOpen = state === 'open';
  const isBusy = state === 'connecting' || state === 'closing';
  const sm = STATE_META[state] || STATE_META.idle;
  const estCost = perMinuteUsd > 0 ? (elapsedMs / 60000) * perMinuteUsd : 0;

  const wssShown = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, { endpoint: endpoint._raw?.id || endpoint.id });
  const codeJs = `// 1) Mint a short-lived ws-token (JWT-authed REST call)
const { token } = await fetch('/api/keys/ws-token', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken },
  body: JSON.stringify({ endpointId: '${endpoint._raw?.id || endpoint.id}' }),
}).then(r => r.json());

// 2) Connect — the browser never uses a long-lived API key
const ws = new WebSocket(
  '${wssShown}',
  ['xapi-ws-v1', 'xapi-key.' + token]
);
ws.binaryType = 'arraybuffer';
ws.onopen = () => ws.send('hello');
ws.onmessage = (e) => console.log(e.data);`;
  const wscat = `wscat -c "${wssShown}" -s xapi-ws-v1 -s xapi-key.<WS_TOKEN>`;

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      {/* Connection card */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">WebSocket console</div>
          <div className="row gap-2" style={{ alignItems: 'center', fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: sm.color }} />
            <span style={{ color: sm.color }}>{sm.label}{closeInfo ? ` · ${closeInfo.code}` : ''}</span>
          </div>
        </div>
        <div style={{ padding: 14 }}>
          <div className="row gap-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
            {!isOpen ? (
              <button className="btn btn-primary" onClick={connect} disabled={isBusy}
                style={{ height: 34, minWidth: 130, justifyContent: 'center' }}>
                {state === 'connecting'
                  ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Connecting…</>
                  : <><XIcons.IconZap size={12} /> Connect</>}
              </button>
            ) : (
              <button className="btn" onClick={disconnect} disabled={isBusy}
                style={{ height: 34, minWidth: 130, justifyContent: 'center', borderColor: 'var(--err)', color: 'var(--err)' }}>
                <XIcons.IconPause size={12} /> Disconnect
              </button>
            )}
            <div className="row gap-3" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
              <span>Duration <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtDuration(elapsedMs)}</span></span>
              {perMinuteUsd > 0 && (
                <span>Est. cost <span className="mono" style={{ color: 'var(--fg-1)' }}>${estCost.toFixed(6)}</span> <span style={{ color: 'var(--fg-4)' }}>(${perMinuteUsd}/min)</span></span>
              )}
            </div>
          </div>
          {connectError && (
            <div className="row gap-2" style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 6, fontSize: 12, color: 'var(--err)' }}>
              <XIcons.IconInfo size={12} /> <span>{connectError}</span>
            </div>
          )}
          <div className="mono" style={{ marginTop: 10, fontSize: 11, color: 'var(--fg-4)', overflowWrap: 'anywhere' }}>
            {wssShown}
          </div>
        </div>
      </div>

      {/* Frame log */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">Frames · {log.length}</div>
          <button className="btn btn-sm btn-ghost" onClick={() => setLog([])} disabled={!log.length}>
            <XIcons.IconTrash size={11} /> Clear
          </button>
        </div>
        <div style={{ padding: 12, maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {log.length === 0 ? (
            <div style={{ color: 'var(--fg-4)', fontSize: 12, textAlign: 'center', padding: '24px 0' }}>
              No frames yet. Connect and send a frame.
            </div>
          ) : log.map((e) => <FrameRow key={e._id} e={e} />)}
        </div>
      </div>

      {/* Send frame */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">Send frame</div>
        </div>
        <div style={{ padding: 14 }}>
          <textarea
            className="input mono"
            value={outText}
            onChange={(e) => setOutText(e.target.value)}
            placeholder={isOpen ? 'Text frame payload…' : 'Connect first to send frames'}
            disabled={!isOpen}
            rows={3}
            style={{ width: '100%', fontSize: 12, resize: 'vertical' }}
          />
          <div className="row gap-2" style={{ marginTop: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" onClick={sendText} disabled={!isOpen || !outText}>
              <XIcons.IconArrowUp size={11} /> Send text
            </button>
            <input ref={fileInputRef} type="file" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) sendBinary(f); e.target.value = ''; }} />
            <button className="btn btn-sm" onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={!isOpen}>
              <XIcons.IconDownload size={11} style={{ transform: 'rotate(180deg)' }} /> Send binary file
            </button>
          </div>
        </div>
      </div>

      {/* Code examples */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">Connect from code</div>
        </div>
        <div style={{ padding: 14 }}>
          <div className="row between" style={{ marginBottom: 6 }}>
            <span className="field-label mono" style={{ margin: 0 }}>JavaScript</span>
            <XPrim.CopyBtn text={codeJs} />
          </div>
          <pre className="code" style={{ margin: 0, maxHeight: 240, fontSize: 11.5, whiteSpace: 'pre-wrap' }}>{codeJs}</pre>
          <div className="row between" style={{ margin: '14px 0 6px' }}>
            <span className="field-label mono" style={{ margin: 0 }}>wscat</span>
            <XPrim.CopyBtn text={wscat} />
          </div>
          <pre className="code" style={{ margin: 0, fontSize: 11.5, whiteSpace: 'pre-wrap' }}>{wscat}</pre>
          <div className="micro-mono" style={{ marginTop: 8, color: 'var(--fg-4)', textTransform: 'none', letterSpacing: 0 }}>
            Replace <span className="mono">{'<WS_TOKEN>'}</span> with a token minted from <span className="mono">POST /keys/ws-token</span>. It is short-lived, endpoint-bound, and can be reused for reconnects until <span className="mono">latestStartAt</span>.
          </div>
        </div>
      </div>
    </div>
  );
}

function FrameRow({ e }) {
  const time = new Date(e.ts).toLocaleTimeString();
  if (e.dir === 'sys') {
    return (
      <div className="mono" style={{ fontSize: 11.5, color: 'var(--fg-4)', display: 'flex', gap: 8 }}>
        <span style={{ flexShrink: 0 }}>{time}</span>
        <span style={{ overflowWrap: 'anywhere' }}>{e.text}</span>
      </div>
    );
  }
  const inbound = e.dir === 'in';
  const color = inbound ? 'var(--ok)' : 'var(--c-blue, #3b82f6)';
  const arrow = inbound ? '↓ IN' : '↑ OUT';
  return (
    <div style={{ borderLeft: `2px solid ${color}`, paddingLeft: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-4)', display: 'flex', gap: 8 }}>
        <span style={{ color }}>{arrow}</span>
        <span>{time}</span>
        <span>{e.kind === 'binary' ? `binary · ${e.size} B` : 'text'}</span>
      </div>
      {e.kind === 'binary'
        ? <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', overflowWrap: 'anywhere' }}>{e.hex}</div>
        : <div className="mono" style={{ fontSize: 11.5, color: 'var(--fg-2)', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{e.text}</div>}
    </div>
  );
}
